import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import SweetAlert from "react-bootstrap-sweetalert";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import IconButton from "@material-ui/core/IconButton";
import DeleteIcon from "@material-ui/icons/Delete";
import Edit from "@material-ui/icons/Edit";
import Box from "@material-ui/core/Box";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import AddIcon from "@material-ui/icons/Add";
import { AppFloatActionByn } from "../../components/AppFloatActionBtn";
import LabSolutionFormDialog from "./LabSolutionFormDialog";
import RegularButton from "../../components/CustomButtons/Button";
import LabSolutionsService from "../../services/LabSolutionsService.js";
import { SHOW_APP_ALERT } from "../../action-types";

export default function LabSolutionsTable() {
  const dispatch = useDispatch();

  const [items, setItems] = useState([]);
  const [deleteAlert, setDeleteAlert] = useState({ show: false, id: null });
  const [publishAlert, setPublishAlert] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState("");

  useEffect(() => {
    getAllItems();
  }, []);

  const getAllItems = async () => {
    const data = await LabSolutionsService.getAllItems();
    setItems(
      data.map((item) => ({
        id: item.Id,
        name: item.Name,
        category: item.Category,
        prompt: item.Prompt,
        prompt_description: item.PromptDescription,
        rank: item.Rank * 1,
      }))
    );
  };

  const deleteItem = (id) => {
    setDeleteAlert({ show: true, id });
  };

  const handleDeleteConfirm = async () => {
    setDeleteAlert({ show: false, id: null });
    const res = await LabSolutionsService.deleteItem(deleteAlert.id);
    dispatch({
      type: SHOW_APP_ALERT,
      alertConfig: {
        type: "success",
        message: "LAB Solution deleted successfully!",
      },
    });
    getAllItems();
  };

  const handleDeleteCancel = () => {
    setDeleteAlert({ show: false, id: null });
  };

  const handleEditOpen = (item) => {
    setEditingItem(item);
    setIsFormOpen(true);
  };
  const handleEditClose = () => {
    setEditingItem(null);
    setIsFormOpen(false);
  };

  const handleFormSubmit = async (formData) => {
    try {
      if (editingItem && editingItem.id) {
        await LabSolutionsService.editItem(formData);
      } else {
        await LabSolutionsService.createItem(formData);
      }
      dispatch({
        type: SHOW_APP_ALERT,
        alertConfig: {
          type: "success",
          message: "LAB Solution saved successfully!",
        },
      });
      getAllItems();
    } catch (error) {
      console.error("Error saving LAB Solution:", error);
    }
  };

  const handlePublishConfirm = async () => {
    setPublishAlert(false);
    const res = await LabSolutionsService.publishItems();
    dispatch({
      type: SHOW_APP_ALERT,
      alertConfig: {
        type: "success",
        message: "LAB Solutions published successfully!",
      },
    });
  };

  const handlePublishClick = () => {
    setPublishAlert(true);
  };

  const filteredItems = (categoryFilter
    ? items.filter((item) => item.category === categoryFilter)
    : items
  ).sort((a, b) => a.rank - b.rank);

  const categories = [
    "IOS Design",
    "IOS Analysis",
  ];

  return (
    <Box style={{ width: "100%", padding: "24px" }}>
      <Box
        mb={2}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "10px",
          width: "100%",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <RegularButton size={"sm"} onClick={handlePublishClick} color="info">
            Publish
          </RegularButton>
          <FormControl variant="outlined" size="small" style={{ minWidth: 180 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              label="Category"
            >
              <MenuItem value="">All</MenuItem>
              {categories.map((cat) => (
                <MenuItem key={cat} value={cat}>
                  {cat}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <AppFloatActionByn color={"cyan"} onClick={() => setIsFormOpen(true)} />
      </Box>

      <div style={{ width: "100%", overflowX: "auto" }}>
        <Table style={{ width: "100%", minWidth: "650px" }}>
          <TableHead>
            <TableRow>
              <TableCell style={{fontWeight: "bold" }}>
                Rank
              </TableCell>
              <TableCell style={{ width: "10%", fontWeight: "bold" }}>
                Category
              </TableCell>
              <TableCell style={{ width: "10%", fontWeight: "bold" }}>
                Name
              </TableCell>
              <TableCell style={{fontWeight: "bold" }}>
                Prompt
              </TableCell>
              <TableCell style={{fontWeight: "bold" }}>
                Description
              </TableCell>
              <TableCell style={{ fontWeight: "bold" }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredItems &&
              filteredItems.map((item, index) => (
                <TableRow key={index}>
                  <TableCell>{item.rank}</TableCell>
                  <TableCell>{item.category}</TableCell>
                  <TableCell>{item.name}</TableCell>
                  <TableCell>{item.prompt}</TableCell>
                  <TableCell>{item.prompt_description}</TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex" }}>
                        <IconButton onClick={() => deleteItem(item.id)}>
                            <DeleteIcon />
                        </IconButton>
                        <IconButton onClick={() => handleEditOpen(item)}>
                            <Edit />
                        </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>

      <LabSolutionFormDialog
        editData={editingItem}
        open={isFormOpen}
        onClose={handleEditClose}
        onSubmit={handleFormSubmit}
      />

      <SweetAlert
        warning
        title="Are you sure?"
        show={deleteAlert.show}
        showConfirm={false}
      >
        <Box>
          You will not be able to recover this item!
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              gap: "10px",
              mt: "10px",
            }}
          >
            <RegularButton
              size={"sm"}
              onClick={handleDeleteConfirm}
              color="info"
            >
              Yes, delete it!
            </RegularButton>
            <RegularButton
              size={"sm"}
              onClick={handleDeleteCancel}
              color="tumblr"
            >
              Cancel
            </RegularButton>
          </Box>
        </Box>
      </SweetAlert>

      <SweetAlert
        warning
        title="Are you sure?"
        show={publishAlert}
        showConfirm={false}
      >
        <Box>
          Do you want to publish all LAB Solutions?
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              gap: "10px",
              mt: "10px",
            }}
          >
            <RegularButton
              size={"sm"}
              onClick={handlePublishConfirm}
              color="info"
            >
              Yes, publish it!
            </RegularButton>
            <RegularButton
              size={"sm"}
              onClick={() => setPublishAlert(false)}
              color="tumblr"
            >
              Cancel
            </RegularButton>
          </Box>
        </Box>
      </SweetAlert>
    </Box>
  );
}
